import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Grid3X3, X, ArrowLeft, Maximize2, Minimize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SEO from '@/components/shared/SEO';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/utils/analytics';

const TOTAL_PAGES = 102;

const CatalogueViewerPage = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [showThumbnails, setShowThumbnails] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const containerRef = useRef(null);
  const imageRef = useRef(null);

  const goToPage = useCallback((page) => {
    const p = Math.max(1, Math.min(TOTAL_PAGES, page));
    setCurrentPage(p);
    setZoom(1);
    setIsLoading(true);
  }, []);

  const prevPage = useCallback(() => goToPage(currentPage - 1), [currentPage, goToPage]);
  const nextPage = useCallback(() => goToPage(currentPage + 1), [currentPage, goToPage]);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowLeft') prevPage();
      else if (e.key === 'ArrowRight') nextPage();
      else if (e.key === 'Escape') {
        if (showThumbnails) setShowThumbnails(false);
        else if (isFullscreen) setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [prevPage, nextPage, showThumbnails, isFullscreen]);

  // Fullscreen
  const toggleFullscreen = () => {
    if (!isFullscreen) {
      containerRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // Fire a single GA4 conversion event when the catalogue is opened
  useEffect(() => {
    trackEvent('catalogue_opened', { source: 'catalogue_viewer_page' });
  }, []);

  const pageSrc = `/Assets/catalogue/pages/${currentPage}.jpg`;

  return (
    <>
      <SEO title="Product Catalogue | Portico Systems" description="Browse the full Portico Systems product catalogue" />

      <div
        ref={containerRef}
        className={cn(
          "flex flex-col bg-stone-900 text-white",
          isFullscreen ? "h-screen" : "min-h-screen"
        )}
        data-testid="catalogue-viewer"
      >
        {/* Top Bar */}
        <header className="flex items-center justify-between px-4 md:px-6 py-3 bg-stone-950/80 backdrop-blur-sm border-b border-white/10 z-20">
          <div className="flex items-center gap-3">
            <Link
              to="/products"
              className="flex items-center gap-1.5 text-sm text-stone-400 hover:text-white transition-colors"
              data-testid="catalogue-back-link"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back to Products</span>
            </Link>
            <span className="text-white/20">|</span>
            <h1 className="text-sm font-semibold tracking-wide">Portico Systems Catalogue</h1>
          </div>

          <div className="flex items-center gap-2">
            {/* Zoom controls */}
            <div className="hidden sm:flex items-center gap-1 bg-white/10 rounded-full px-1">
              <button
                onClick={() => setZoom(z => Math.max(0.5, z - 0.25))}
                className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
                data-testid="catalogue-zoom-out"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <span className="text-xs w-12 text-center font-mono">{Math.round(zoom * 100)}%</span>
              <button
                onClick={() => setZoom(z => Math.min(3, z + 0.25))}
                className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
                data-testid="catalogue-zoom-in"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
            </div>

            {/* Thumbnails toggle */}
            <button
              onClick={() => setShowThumbnails(s => !s)}
              className={cn(
                "p-2 rounded-full transition-colors",
                showThumbnails ? "bg-white/20 text-white" : "hover:bg-white/10 text-stone-400"
              )}
              data-testid="catalogue-thumbnails-toggle"
            >
              <Grid3X3 className="w-4 h-4" />
            </button>

            {/* Fullscreen */}
            <button
              onClick={toggleFullscreen}
              className="p-2 rounded-full hover:bg-white/10 text-stone-400 transition-colors"
              data-testid="catalogue-fullscreen-toggle"
            >
              {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
          </div>
        </header>

        {/* Main content area */}
        <div className="flex-1 flex relative overflow-hidden">
          {/* Thumbnail sidebar */}
          {showThumbnails && (
            <aside className="w-48 md:w-56 bg-stone-950 border-r border-white/10 overflow-y-auto flex-shrink-0 z-10">
              <div className="p-3 grid grid-cols-2 gap-2">
                {Array.from({ length: TOTAL_PAGES }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => { goToPage(page); setShowThumbnails(false); }}
                    className={cn(
                      "group relative rounded overflow-hidden border-2 transition-all",
                      page === currentPage
                        ? "border-primary ring-1 ring-primary/50"
                        : "border-transparent hover:border-white/30"
                    )}
                    data-testid={`catalogue-thumb-${page}`}
                  >
                    <img
                      src={`/Assets/catalogue/thumbs/${page}.jpg`}
                      alt={`Page ${page}`}
                      className="w-full aspect-[3/4] object-cover"
                      loading="lazy"
                    />
                    <span className="absolute bottom-0 inset-x-0 bg-black/70 text-[10px] text-center py-0.5 font-mono">
                      {page}
                    </span>
                  </button>
                ))}
              </div>
            </aside>
          )}

          {/* Page viewer */}
          <div className="flex-1 flex items-center justify-center relative overflow-auto">
            {/* Prev button */}
            <button
              onClick={prevPage}
              disabled={currentPage === 1}
              className="absolute left-2 md:left-4 z-10 p-2 md:p-3 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur-sm transition-all disabled:opacity-20 disabled:cursor-not-allowed"
              data-testid="catalogue-prev-btn"
            >
              <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
            </button>

            {/* Page image */}
            <div className="flex items-center justify-center p-4 w-full h-full">
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                </div>
              )}
              <img
                ref={imageRef}
                src={pageSrc}
                alt={`Catalogue page ${currentPage}`}
                className="max-h-[calc(100vh-120px)] w-auto object-contain transition-transform duration-200 select-none"
                style={{ transform: `scale(${zoom})`, transformOrigin: 'center center' }}
                onLoad={() => setIsLoading(false)}
                draggable={false}
                data-testid="catalogue-page-image"
              />
            </div>

            {/* Next button */}
            <button
              onClick={nextPage}
              disabled={currentPage === TOTAL_PAGES}
              className="absolute right-2 md:right-4 z-10 p-2 md:p-3 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur-sm transition-all disabled:opacity-20 disabled:cursor-not-allowed"
              data-testid="catalogue-next-btn"
            >
              <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
            </button>
          </div>
        </div>

        {/* Bottom bar */}
        <footer className="flex items-center justify-center gap-4 px-4 py-2.5 bg-stone-950/80 backdrop-blur-sm border-t border-white/10">
          <Button
            variant="ghost"
            size="sm"
            onClick={prevPage}
            disabled={currentPage === 1}
            className="text-stone-400 hover:text-white disabled:opacity-30"
            data-testid="catalogue-prev-footer"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Previous
          </Button>

          <div className="flex items-center gap-2">
            <input
              type="number"
              min={1}
              max={TOTAL_PAGES}
              value={currentPage}
              onChange={e => goToPage(parseInt(e.target.value) || 1)}
              className="w-14 text-center text-sm bg-white/10 border border-white/20 rounded px-2 py-1 text-white focus:outline-none focus:ring-1 focus:ring-primary"
              data-testid="catalogue-page-input"
            />
            <span className="text-sm text-stone-500">of {TOTAL_PAGES}</span>
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={nextPage}
            disabled={currentPage === TOTAL_PAGES}
            className="text-stone-400 hover:text-white disabled:opacity-30"
            data-testid="catalogue-next-footer"
          >
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </footer>
      </div>
    </>
  );
};

export default CatalogueViewerPage;
